import { useContext, useMemo, useRef, useState } from "react";
import MainModal from "components/MainModal";
import { useTranslation } from "react-i18next";
import { useStyles } from "./CreateEditManualRCAModal.styles";
import { Formik, FormikProps, Form } from "formik";
import { useRCAMainStyles } from "../../../../../RCA.styles";
import { createRCAFormValidationSchema } from "validations/schemas";
import {
    Box,
    Button,
    Divider,
    FormHelperText,
    InputLabel,
    TextField,
    Typography,
    Snackbar,
    Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { RCA_STATUSES, RcaTypeEnum, USERTYPE } from "constants/constants";
import DoneIcon from "@mui/icons-material/Done";
import useDateFormat from "hooks/useDateFormat";
import dayjs from "dayjs";
import RCAStatusDropdown from "pages/Corporate/RCA/components/RCAStatusDropdown";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import { ICreateEditManualRCAModal, ICreateEditManualRCAModalFormValues } from "./CreateEditManualRCAModalInterfaces";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import { IOption } from "components/NewMainSelect/NewMainSelectInterfaces";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import moment from "moment";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";

const CreateEditManualRCAModal = ({ open, onClose, onAddRCA, rcaEditionValues }: ICreateEditManualRCAModal) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { getDateFormatByLanguage } = useDateFormat();
    const formRef = useRef<FormikProps<ICreateEditManualRCAModalFormValues>>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbarFailureMessage, setSnackbarFailureMessage] = useState("");

    const { currentUser } = useContext(CorporateContext);

    const { upsertRCADetailsMutation } = RCALibraryEntity.UpsertRCADetails();
    const { mills, loading } = RCALibraryEntity.GetMills();

    const millsOptions: IOption[] = useMemo(
        () => mills.map((m) => ({ id: m.mill_id, name: m.name as string })),
        [mills],
    );

    const isEdition = useMemo(() => Boolean(rcaEditionValues), [rcaEditionValues]);

    const isRCAManual = useMemo(
        () => rcaEditionValues?.rca_type?.rca_type_id === RcaTypeEnum.MANUAL,
        [rcaEditionValues],
    );

    const isAdmin = useMemo(
        () =>
            (currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
                currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE) &&
            (isEdition ? isRCAManual : true),
        [currentUser, isEdition, isRCAManual],
    );

    const initFormValues: ICreateEditManualRCAModalFormValues = useMemo(
        () =>
            isEdition
                ? {
                      rcaDescription: rcaEditionValues?.incident_description || "",
                      statusId: rcaEditionValues?.status?.status_id || RCA_STATUSES.NOT_STARTED,
                      dueDate: rcaEditionValues?.due_date || new Date(),
                      incidentDate: rcaEditionValues?.incident_date || rcaEditionValues?.created_at!,
                      ownerId: rcaEditionValues?.owner?.user_id || "",
                      productionLineId: rcaEditionValues?.rca_detail?.production_line?.production_line_id || "",
                      machineCenterId: rcaEditionValues?.rca_detail?.machine_center?.machine_center_id || "",
                      functionalLocationId:
                          rcaEditionValues?.rca_detail?.functional_location?.functional_location_id || "",
                      failureTypeId: rcaEditionValues?.rca_detail?.failure_type?.failure_type_id || "",
                      failureCodeId: rcaEditionValues?.rca_detail?.failure_code?.failure_code_id || "",
                      secondaryFailureCodeId:
                          rcaEditionValues?.rca_detail?.secondary_failure_code?.failure_code_id || "",
                      shiftId: rcaEditionValues?.rca_detail?.shift?.shift_id || "",
                      isAdmin: isAdmin,
                      millId: rcaEditionValues?.mill_id || "",
                      meetingTypeId: rcaEditionValues?.meeting_type_id || "",
                  }
                : {
                      rcaDescription: "",
                      statusId: RCA_STATUSES.NOT_STARTED,
                      dueDate: moment(new Date()).add(4, "days").toDate(),
                      incidentDate: new Date(),
                      ownerId: "",
                      productionLineId: "",
                      machineCenterId: "",
                      functionalLocationId: "",
                      secondaryFunctionalLocationId: "",
                      failureTypeId: "",
                      failureCodeId: "",
                      secondaryFailureCodeId: "",
                      shiftId: "",
                      isAdmin: isAdmin,
                      millId: "",
                      meetingTypeId: "",
                  },
        [isAdmin, rcaEditionValues, isEdition],
    );

    const handleSubmit = async (values: ICreateEditManualRCAModalFormValues) => {
        setIsSaving(true);
        const rcaDetailsSelected =
            values.productionLineId ||
            values.machineCenterId ||
            values.functionalLocationId ||
            values.shiftId ||
            values.failureTypeId ||
            values.failureCodeId ||
            values.secondaryFailureCodeId;

        const rcaValues = {
            ...(rcaEditionValues?.rca_id ? { rca_id: rcaEditionValues?.rca_id } : {}),
            mill_id: values.millId || currentUser?.mill?.mill_id,
            incident_description: values.rcaDescription,
            rca_type_id: isEdition ? rcaEditionValues?.rca_type.rca_type_id : RcaTypeEnum.MANUAL,
            owner_id: values.ownerId,
            status_id: values.statusId,
            due_date: values.dueDate,
            ...((isEdition && isRCAManual) || !isEdition ? { incident_date: values.incidentDate } : {}),
            ...(values.meetingTypeId && { meeting_type_id: values.meetingTypeId }),
            ...(!isEdition ? { created_by: currentUser?.user_id } : {}),
            ...(isEdition && rcaEditionValues?.rca_settings_id
                ? { rca_settings_id: rcaEditionValues?.rca_settings_id }
                : {}),
            ...(!isEdition && rcaDetailsSelected
                ? {
                      rca_detail: {
                          data: {
                              ...(values.productionLineId ? { production_line_id: values.productionLineId } : {}),
                              ...(values.machineCenterId ? { machine_center_id: values.machineCenterId } : {}),
                              ...(values.functionalLocationId
                                  ? { functional_location_id: values.functionalLocationId }
                                  : {}),
                              ...(values.shiftId ? { shift_id: values.shiftId } : {}),
                              ...(values.failureTypeId ? { failure_type_id: values.failureTypeId } : {}),
                              ...(values.failureCodeId ? { failure_code_id: values.failureCodeId } : {}),
                              ...(values.secondaryFailureCodeId
                                  ? { secondary_failure_code_id: values.secondaryFailureCodeId }
                                  : {}),
                          },
                      },
                  }
                : {}),
        };

        const rcaDetailsUpdateValues = isRCAManual
            ? {
                  ...(rcaEditionValues?.rca_detail?.id ? { id: rcaEditionValues?.rca_detail?.id } : {}),
                  rca_id: rcaEditionValues?.rca_id,
                  production_line_id: values.productionLineId || null,
                  machine_center_id: values.machineCenterId || null,
                  functional_location_id: values.functionalLocationId || null,
                  shift_id: values.shiftId || null,
                  failure_type_id: values.failureTypeId || null,
                  failure_code_id: values.failureCodeId || null,
                  secondary_failure_code_id: values.secondaryFailureCodeId || null,
              }
            : {};

        try {
            const response = await upsertRCADetailsMutation({
                variables: {
                    rca: rcaValues,
                    isUpdate: Boolean(rcaDetailsSelected && isRCAManual),
                    rcaDetails: rcaDetailsUpdateValues,
                },
            });

            setIsSaving(false);
            if (response) {
                const newRCA: { rca_id: number } = response.data?.insert_rca_one;
                if (newRCA) {
                    await onAddRCA(newRCA);
                } else {
                    setSnackbarFailureMessage(t("rca.rca_library.rca_detail.create_rca_modal.creation_error"));
                }
            } else {
                setSnackbarFailureMessage(t("rca.rca_library.rca_detail.create_rca_modal.creation_error"));
            }
        } catch {
            setIsSaving(false);
            return;
        }
    };

    const footer = (
        <div className={classes.footer}>
            <Button variant="text" color="info" onClick={onClose}>
                {t("main_modal.cancel_button")}
            </Button>
            <Button
                variant="contained"
                color="info"
                onClick={() => {
                    if (formRef.current) {
                        formRef.current.handleSubmit();
                    }
                }}
            >
                <DoneIcon />
                {isEdition
                    ? t("rca.rca_library.rca_detail.save_changes")
                    : t("rca.rca_library.rca_detail.create_rca_modal.create_rca_button")}
            </Button>
        </div>
    );

    return (
        <MainModal
            isOpen={open}
            onClose={onClose}
            title={
                isEdition
                    ? t("rca.rca_library.rca_detail.edit_rca")
                    : t("rca.rca_library.rca_detail.create_rca_modal.title")
            }
            showCloseButton
            customModalStyles={{ width: "90%", maxWidth: 1000 }}
            footer={footer}
        >
            <Formik
                innerRef={formRef}
                initialValues={initFormValues}
                validationSchema={createRCAFormValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, values, handleChange, setFieldValue }) => {
                    const {
                        productionLineOptions,
                        machineCenterOptions,
                        functionalLocationOptions,
                        failureTypeOptions,
                        failureCodeOptions,
                        shiftOptions,
                        secondaryFailureCodeOptions,
                        loadingProductionLines,
                        loadingMachineCenters,
                        loadingFailureTypes,
                        loadingFunctionalLocations,
                        loadingFailureCodes,
                        loadingShifts,
                        loadingSecondaryFailureCodes,
                        apolloQueryError,
                    } = RCALibraryEntity.GetRCADetailsOptions(values);
                    return (
                        <Form>
                            <div className={classes.mainContainer}>
                                <div
                                    className={
                                        (isEdition && isRCAManual) || !isEdition
                                            ? classes.leftContainer
                                            : classes.onlyContainer
                                    }
                                >
                                    <div className={classes.leftContentWrapper}>
                                        <div className={classes.form}>
                                            <Box className={mainClasses.contentFormBox} style={{ height: "190px" }}>
                                                <InputLabel id="outlined-rcaDescription-label">
                                                    <Typography
                                                        variant="subtitle2"
                                                        className={mainClasses.contentFormLabel}
                                                    >
                                                        {t(
                                                            "rca.rca_library.rca_detail.create_rca_modal.rca_description",
                                                        )}
                                                    </Typography>
                                                </InputLabel>
                                                <TextField
                                                    disabled={isSaving}
                                                    aria-labelledby="outlined-rcaDescription-label"
                                                    fullWidth
                                                    id="outlined-rcaDescription-input"
                                                    className={mainClasses.contentFormTextField}
                                                    name="rcaDescription"
                                                    type="text"
                                                    variant="outlined"
                                                    rows={3}
                                                    multiline
                                                    value={values.rcaDescription}
                                                    onChange={handleChange}
                                                    error={touched.rcaDescription && Boolean(errors.rcaDescription)}
                                                    helperText={
                                                        touched.rcaDescription &&
                                                        errors.rcaDescription &&
                                                        t(errors.rcaDescription)
                                                    }
                                                />
                                            </Box>
                                            {(isRCAManual || !isEdition) && (
                                                <>
                                                    {" "}
                                                    <Divider />
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "16px",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle2"
                                                            className={mainClasses.contentFormLabel}
                                                        >
                                                            {t(
                                                                "rca.rca_library.rca_detail.create_rca_modal.rca_details",
                                                            )}
                                                        </Typography>
                                                        {isAdmin && !values.millId && (
                                                            <Alert
                                                                sx={{ alignSelf: "center" }}
                                                                className={classes.alertInfo}
                                                                severity="info"
                                                            >
                                                                {t(
                                                                    "rca.rca_library.rca_detail.create_rca_modal.rca_detail_info_alert",
                                                                )}
                                                            </Alert>
                                                        )}
                                                        <div className={classes.extraDetailsContent}>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.production_line",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        withSearchBar
                                                                        disabled={
                                                                            isAdmin
                                                                                ? loadingProductionLines ||
                                                                                  !values.millId
                                                                                : loadingProductionLines
                                                                        }
                                                                        placeholder={
                                                                            loadingProductionLines
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_production_line",
                                                                                  )
                                                                        }
                                                                        options={productionLineOptions}
                                                                        onChange={(value) => {
                                                                            setFieldValue("productionLineId", value);
                                                                            setFieldValue("machineCenterId", "");
                                                                            setFieldValue("functionalLocationId", "");
                                                                            setFieldValue("failureTypeId", "");
                                                                            setFieldValue("failureCodeId", "");
                                                                            setFieldValue("secondaryFailureCodeId", "");
                                                                            setFieldValue("shiftId", "");
                                                                        }}
                                                                        value={values.productionLineId}
                                                                    />
                                                                </>
                                                            </Box>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.machine_center",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        disabled={
                                                                            !values.productionLineId ||
                                                                            loadingMachineCenters
                                                                        }
                                                                        placeholder={
                                                                            loadingMachineCenters
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_machine_center",
                                                                                  )
                                                                        }
                                                                        withSearchBar
                                                                        options={machineCenterOptions}
                                                                        onChange={(value) => {
                                                                            setFieldValue("machineCenterId", value);
                                                                            setFieldValue("functionalLocationId", "");
                                                                            setFieldValue("failureTypeId", "");
                                                                            setFieldValue("failureCodeId", "");
                                                                            setFieldValue("secondaryFailureCodeId", "");
                                                                            setFieldValue("shiftId", "");
                                                                        }}
                                                                        value={values.machineCenterId}
                                                                    />
                                                                </>
                                                            </Box>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.functional_location",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        disabled={
                                                                            !values.machineCenterId ||
                                                                            loadingFunctionalLocations
                                                                        }
                                                                        withSearchBar
                                                                        placeholder={
                                                                            loadingFunctionalLocations
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_functional_location",
                                                                                  )
                                                                        }
                                                                        options={functionalLocationOptions}
                                                                        onChange={(value) => {
                                                                            setFieldValue(
                                                                                "functionalLocationId",
                                                                                value,
                                                                            );
                                                                            setFieldValue("failureTypeId", "");
                                                                            setFieldValue("failureCodeId", "");
                                                                            setFieldValue("secondaryFailureCodeId", "");
                                                                            setFieldValue("shiftId", "");
                                                                        }}
                                                                        value={values.functionalLocationId}
                                                                    />
                                                                </>
                                                            </Box>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.shift",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        disabled={
                                                                            isAdmin
                                                                                ? !values.millId || loadingShifts
                                                                                : loadingShifts
                                                                        }
                                                                        options={shiftOptions}
                                                                        placeholder={
                                                                            loadingShifts
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_shift",
                                                                                  )
                                                                        }
                                                                        withSearchBar
                                                                        onChange={(value) => {
                                                                            setFieldValue("shiftId", value);
                                                                        }}
                                                                        value={values.shiftId}
                                                                    />
                                                                </>
                                                            </Box>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.failure_types",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        withSearchBar
                                                                        disabled={
                                                                            isAdmin
                                                                                ? !values.millId || loadingFailureTypes
                                                                                : loadingFailureTypes
                                                                        }
                                                                        placeholder={
                                                                            loadingFailureTypes
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_failure_types",
                                                                                  )
                                                                        }
                                                                        options={failureTypeOptions}
                                                                        onChange={(value) => {
                                                                            setFieldValue("failureTypeId", value);
                                                                            setFieldValue("failureCodeId", "");
                                                                            setFieldValue("secondaryFailureCodeId", "");
                                                                        }}
                                                                        value={values.failureTypeId}
                                                                    />
                                                                </>
                                                            </Box>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.failure_codes",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        disabled={
                                                                            !values.failureTypeId || loadingFailureCodes
                                                                        }
                                                                        withSearchBar
                                                                        placeholder={
                                                                            loadingFailureCodes
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_failure_codes",
                                                                                  )
                                                                        }
                                                                        options={failureCodeOptions}
                                                                        onChange={(value) => {
                                                                            setFieldValue("failureCodeId", value);
                                                                            setFieldValue("secondaryFailureCodeId", "");
                                                                        }}
                                                                        value={values.failureCodeId}
                                                                    />
                                                                </>
                                                            </Box>
                                                            <Box
                                                                className={`${mainClasses.contentFormBox} ${mainClasses.noOverflow} ${classes.dropdownContent}`}
                                                            >
                                                                <InputLabel>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        className={mainClasses.contentFormLabel}
                                                                    >
                                                                        {t(
                                                                            "rca.rca_library.rca_detail.create_rca_modal.secondary_failure_codes",
                                                                        )}
                                                                    </Typography>
                                                                </InputLabel>

                                                                <>
                                                                    <NewMainSelect
                                                                        fullWidth
                                                                        disabled={
                                                                            !values.failureCodeId ||
                                                                            loadingSecondaryFailureCodes
                                                                        }
                                                                        withSearchBar
                                                                        placeholder={
                                                                            loadingSecondaryFailureCodes
                                                                                ? t("data.loading")
                                                                                : t(
                                                                                      "rca.rca_library.rca_detail.create_rca_modal.select_secondary_failure_codes",
                                                                                  )
                                                                        }
                                                                        options={secondaryFailureCodeOptions}
                                                                        onChange={(value) => {
                                                                            setFieldValue(
                                                                                "secondaryFailureCodeId",
                                                                                value,
                                                                            );
                                                                        }}
                                                                        value={values.secondaryFailureCodeId}
                                                                    />
                                                                </>
                                                            </Box>
                                                        </div>
                                                    </div>
                                                    <p
                                                        style={{
                                                            display: apolloQueryError ? "block" : "none",
                                                            color: "red",
                                                            width: "100%",
                                                            textAlign: "center",
                                                            margin: 0,
                                                        }}
                                                    >
                                                        {apolloQueryError}
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {!isEdition || isAdmin ? (
                                    <div className={classes.rightContainer}>
                                        <div className={classes.form}>
                                            {!isEdition && (
                                                <>
                                                    <Box className={mainClasses.contentFormBox}>
                                                        <InputLabel id="outlined-dueDate-label">
                                                            <Typography
                                                                variant="subtitle2"
                                                                className={mainClasses.contentFormLabel}
                                                            >
                                                                {t("rca.rca_library.rca_detail.due_date")}
                                                            </Typography>
                                                        </InputLabel>

                                                        <DatePicker
                                                            className={mainClasses.contentFormDatePicker}
                                                            value={dayjs(values.dueDate)}
                                                            disablePast
                                                            format={getDateFormatByLanguage()}
                                                            onChange={(date) => {
                                                                setFieldValue("dueDate", date);
                                                            }}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                            {((isEdition && isRCAManual) || !isEdition) && (
                                                <>
                                                    <Box className={mainClasses.contentFormBox}>
                                                        <InputLabel id="outlined-incidentDate-label">
                                                            <Typography
                                                                variant="subtitle2"
                                                                className={mainClasses.contentFormLabel}
                                                            >
                                                                {t("rca.rca_library.rca_detail.incident_date")}
                                                            </Typography>
                                                        </InputLabel>

                                                        <DatePicker
                                                            className={mainClasses.contentFormDatePicker}
                                                            value={dayjs(values.incidentDate)}
                                                            minDate={dayjs().subtract(1, "year")}
                                                            disableFuture
                                                            format={getDateFormatByLanguage()}
                                                            onChange={(date) => {
                                                                setFieldValue("incidentDate", date);
                                                            }}
                                                        />
                                                    </Box>
                                                    <Divider />
                                                </>
                                            )}
                                            {isAdmin && (
                                                <>
                                                    <Box className={`${mainClasses.contentFormBox}`}>
                                                        <InputLabel>
                                                            <Typography
                                                                variant="subtitle2"
                                                                className={mainClasses.contentFormLabel}
                                                            >
                                                                {t("rca.rca_library.rca_detail.create_rca_modal.mill")}
                                                            </Typography>
                                                        </InputLabel>

                                                        <>
                                                            <NewMainSelect
                                                                fullWidth
                                                                disabled={loading}
                                                                withSearchBar
                                                                placeholder={
                                                                    loading
                                                                        ? t("data.loading")
                                                                        : t(
                                                                              "rca.rca_library.rca_detail.create_rca_modal.select_mill",
                                                                          )
                                                                }
                                                                options={millsOptions}
                                                                onChange={(value) => {
                                                                    setFieldValue("millId", value);
                                                                    setFieldValue("productionLineId", "");
                                                                    setFieldValue("machineCenterId", "");
                                                                    setFieldValue("functionalLocationId", "");
                                                                    setFieldValue("failureTypeId", "");
                                                                    setFieldValue("failureCodeId", "");
                                                                    setFieldValue("secondaryFailureCodeId", "");
                                                                    setFieldValue("shiftId", "");
                                                                    setFieldValue("ownerId", "");
                                                                }}
                                                                value={values.millId}
                                                            />
                                                            {!!errors.millId && !!touched.millId && (
                                                                <FormHelperText
                                                                    className={mainClasses.contentFormHelperText}
                                                                >
                                                                    {t(errors.millId)}
                                                                </FormHelperText>
                                                            )}
                                                        </>
                                                    </Box>
                                                    <Divider />
                                                </>
                                            )}
                                            {!isEdition && (
                                                <>
                                                    <Box className={mainClasses.contentFormBox}>
                                                        <InputLabel id="outlined-ownerId-label">
                                                            <Typography
                                                                variant="subtitle2"
                                                                className={mainClasses.contentFormLabel}
                                                            >
                                                                {t("rca.rca_library.rca_detail.owner")}
                                                            </Typography>
                                                        </InputLabel>
                                                        <RCAUsersDropdown
                                                            fullWidth
                                                            millId={values.millId}
                                                            multiple={false}
                                                            value={values.ownerId}
                                                            onChange={(value) => {
                                                                setFieldValue("ownerId", value);
                                                            }}
                                                            placeholder={t("rca.settings.owners_dropdown.select_owner")}
                                                        />
                                                        {!!errors.ownerId && !!touched.ownerId && (
                                                            <FormHelperText
                                                                className={mainClasses.contentFormHelperText}
                                                            >
                                                                {t(errors.ownerId)}
                                                            </FormHelperText>
                                                        )}
                                                    </Box>
                                                    <Divider />
                                                    <RCAStatusDropdown
                                                        handleChange={(value) => {
                                                            setFieldValue("statusId", value);
                                                        }}
                                                        value={values.statusId}
                                                    />
                                                    <Divider />
                                                    <Box className={mainClasses.contentFormBox}>
                                                        <InputLabel id="outlined-meetingTypeId-label">
                                                            <Typography
                                                                variant="subtitle2"
                                                                className={mainClasses.contentFormLabel}
                                                            >
                                                                {t("rca.rca_library.rca_detail.meeting_type")}
                                                            </Typography>
                                                        </InputLabel>
                                                        <RCAMeetingTypesDropdown
                                                            fullWidth
                                                            multiple={false}
                                                            value={values.meetingTypeId}
                                                            onChange={(value) => {
                                                                setFieldValue("meetingTypeId", value);
                                                            }}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    ((isEdition && isRCAManual) || !isEdition) && (
                                        <div className={classes.rightContainer}>
                                            <div className={classes.form}>
                                                <Box className={mainClasses.contentFormBox}>
                                                    <InputLabel id="outlined-incidentDate-label">
                                                        <Typography
                                                            variant="subtitle2"
                                                            className={mainClasses.contentFormLabel}
                                                        >
                                                            {t("rca.rca_library.rca_detail.incident_date")}
                                                        </Typography>
                                                    </InputLabel>

                                                    <DatePicker
                                                        className={mainClasses.contentFormDatePicker}
                                                        value={dayjs(values.incidentDate)}
                                                        minDate={dayjs().subtract(1, "year")}
                                                        disableFuture
                                                        format={getDateFormatByLanguage()}
                                                        onChange={(date) => {
                                                            setFieldValue("incidentDate", date);
                                                        }}
                                                    />
                                                </Box>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            <Snackbar
                open={snackbarFailureMessage !== ""}
                onClose={() => setSnackbarFailureMessage("")}
                autoHideDuration={6000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity="error" sx={{ width: "100%" }}>
                    {snackbarFailureMessage}
                </Alert>
            </Snackbar>
        </MainModal>
    );
};

export default CreateEditManualRCAModal;
